/**
 * Documentation ▸ Release documents ▸ **one release**
 * (`/documentation/releases/10.2?version=10.2.0.27`, 2026-09-17).
 *
 * A rail of the release's versions (the Releases page's `VersionRail`, over
 * the same `releaseFamilies` nesting) beside the selected version's
 * documents — the SAME `ReleaseDocuments` list the Releases version pane
 * shows, so a version's documents include the ones written against its
 * release, exactly as there. No downloads and no change log: this is the
 * documentation's view of a release, and the Releases page is unchanged.
 *
 * Each document opens in the ordinary reader at `/documentation/:slug`, and
 * the link hands the reader THIS page as its Back, so a reader returns to
 * the version it came from rather than to Releases.
 */
import { Box, Typography } from "@mui/material";
import { BookOpen } from "lucide-react";
import { useCallback, useEffect, useMemo, type JSX } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { BackButton } from "../../components/ui/back-button.js";
import { PageLoader } from "../../components/ui/page-loader.js";
import type { ReleaseRow } from "../portal/releases/api.js";
import { findFamily, versionsOf } from "../portal/releases/release-families.js";
import { VERSION_PARAM } from "../portal/releases/release-paths.js";
import { ReleaseDocuments } from "../portal/releases/ReleaseDocuments.js";
import { useReleases } from "../portal/releases/useReleases.js";
import { VersionRail } from "../portal/releases/VersionRail.js";
import { KB_RELEASES_PATH, kbReleasePath } from "./kb-paths.js";
import { RELEASE_DOCUMENTS_TITLE } from "./ReleaseDocumentsIndexPage.js";

export function ReleaseDocumentsReleasePage(): JSX.Element {
  const { release: releaseParam } = useParams<{ release: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { families, isLoading, isError } = useReleases();

  const family = findFamily(families, releaseParam);
  const versions = useMemo(() => (family ? versionsOf(family) : []), [family]);

  const selectVersion = useCallback(
    (version: string | null): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (version === null) next.delete(VERSION_PARAM);
          else next.set(VERSION_PARAM, version);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const wanted = searchParams.get(VERSION_PARAM);
  const selected: ReleaseRow | undefined = versions.find((v) => v.version === wanted) ?? versions[0];

  useEffect(() => {
    if (wanted && versions.length > 0 && !versions.some((v) => v.version === wanted)) {
      selectVersion(null);
    }
  }, [wanted, versions, selectVersion]);

  const backToList = (
    <BackButton to={KB_RELEASES_PATH} label={RELEASE_DOCUMENTS_TITLE} size="small" sx={{ justifySelf: "start" }} />
  );

  if (isLoading) return <PageLoader />;

  if (isError || !family) {
    return (
      <Box sx={{ display: "grid", gap: 2 }}>
        {backToList}
        <Typography sx={{ py: 6, textAlign: "center", fontSize: 14, color: "text.disabled" }}>
          {isError
            ? "Could not load releases. Try again in a moment."
            : `There is no release ${releaseParam ?? ""}. It may not have shipped yet, or the link may be wrong.`}
        </Typography>
      </Box>
    );
  }

  const { line } = family;

  return (
    <Box sx={{ display: "grid", gap: 2 }} data-testid={`kb-release-page-${line.version}`}>
      {backToList}
      <Box>
        <Typography component="h2" sx={{ fontSize: "1.5rem", fontWeight: 700 }}>
          Release {line.version} documents
        </Typography>
        <Typography sx={{ color: "text.secondary" }}>
          {line.product_name ? `${line.product_name} · ` : ""}
          Pick a version to see every document that ships with it.
        </Typography>
      </Box>

      {versions.length === 0 ? (
        <Typography sx={{ py: 6, textAlign: "center", fontSize: 14, color: "text.disabled" }}>
          No versions in {line.version} have shipped yet.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 2.5,
            gridTemplateColumns: { xs: "1fr", md: "minmax(220px, 260px) minmax(0, 1fr)" },
            alignItems: "start",
          }}
        >
          <VersionRail
            releaseVersion={line.version}
            versions={versions}
            selectedId={selected?.id}
            onSelect={(v) => selectVersion(v.version)}
          />
          {selected && (
            <Box
              component="section"
              key={selected.id}
              aria-label={`Documents for ${selected.version}`}
              data-testid={`kb-version-documents-${selected.version}`}
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
                px: 2.5,
                py: 2,
                display: "grid",
                gap: 1.5,
                minWidth: 0,
              }}
            >
              <Typography component="h3" sx={{ fontSize: 16, fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
                <BookOpen size={16} aria-hidden />
                Version {selected.version}
              </Typography>
              <ReleaseDocuments
                release={selected}
                back={{
                  to: kbReleasePath(line.version, selected.version),
                  label: `Release ${line.version} documents`,
                }}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export default ReleaseDocumentsReleasePage;
